import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';

import { Modal } from '~/eds/src/components/Modal/Modal';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { DOCUMENT_NAME_MAX_LENGTH } from '~/features/document-handler/constants';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { injectIntl } from 'react-intl';
import { getHighlights } from '~/redux/api/methods/highlights';
import { uploadDocumentsThunk } from '~/redux/thunks/fileUpload';

import {
  MODAL_DELETE,
  MODAL_NAME,
  MODAL_VERSION_DOCUMENT_VIEWER,
} from '../../../types/modal.types';
import { ERROR, SUCCESS } from '../../../types/toast.types';
import { createVersionListFromVersionData } from '../../Modals/VersionDocumentViewerModal/VersionDocumentViewerModal.utils';
import EcCard from '../../Shared/EcCard/EcCard';
import EcModal from '../../Shared/EcModal/EcModal';
import EcPaginate from '../../Shared/EcPaginate/EcPaginate';
import { showToast } from '../../Shared/EcToast/showToast';
import EcVersionsTable from '../../Shared/EcVersionsTable/EcVersionsTable';
import LoadingSpinner from '../../Shared/Icons/LoadingSpinner';
import {
  deleteDocumentVersion,
  editDocumentVersion,
  getDocumentVersions,
  replaceDocumentVersion,
  restoreDocumentVersion,
  updateDocumentVersionOrder,
} from './DocumentVersions.data';
import styles from './DocumentVersions.styles';
import { DocumentVersionViewer } from './DocumentVersionViewer';

class DocumentVersions extends Component {
  constructor(props) {
    super(props);

    this.loadDocumentVersions = this.loadDocumentVersions.bind(this);
    this.handlePageClick = this.handlePageClick.bind(this);
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.onVersionReorder = this.onVersionReorder.bind(this);
    this.onVersionEdit = this.onVersionEdit.bind(this);
    this.onVersionReplace = this.onVersionReplace.bind(this);
    this.onVersionRestore = this.onVersionRestore.bind(this);
    this.onVersionDelete = this.onVersionDelete.bind(this);
    this.handleFileAttachment = this.handleFileAttachment.bind(this);

    this.state = {
      page: 1,
      pageCount: 1,
      pageSize: 10,
      filterValue: '',
      selectedItem: null,
      versionsData: [],
      warningModal: {
        isVisible: false,
        item: null,
        file: null,
      },
    };
  }

  componentDidMount() {
    this.loadDocumentVersions();
  }

  loadDocumentVersions(searchText = null) {
    const { documentId } = this.props;
    const { page } = this.state;

    getDocumentVersions(documentId, page, searchText)
      .then((data) => {
        this.setState({
          versionsData: data.results,
          pageCount: data.count,
          loading: false,
          errorLoading: false,
        });
      })
      .catch(() => this.setState({ loading: false, errorLoading: true }));
  }

  handlePageClick(clickedPage) {
    const selectedPage = clickedPage.selected + 1;
    this.setState({ page: selectedPage }, this.loadDocumentVersions);
  }

  handleShowToast(type, text) {
    showToast(type, text);
  }

  handleShowModal(modal, selectedItem) {
    this.setState({
      modal,
      selectedItem,
    });
  }

  handleHideModal() {
    this.setState({
      modal: null,
      selectedItem: null,
    });
  }

  handleVersionSearch = (filterValue) => {
    this.setState({ filterValue });
    this.loadDocumentVersions(filterValue);
  };

  renderDocumentViewerModal() {
    const { selectedItem, pdfFile, versionsData, page, pageCount, pageSize } =
      this.state;

    const { flags } = this.props;

    const versionList = createVersionListFromVersionData(versionsData);

    if (flags[FlagType.UnifyDocumentViewer]) {
      return (
        <DocumentVersionViewer
          onHide={this.handleHideModal}
          selectedVersion={selectedItem}
          versions={versionList}
        />
      );
    }
    return (
      <EcModal
        modalType={MODAL_VERSION_DOCUMENT_VIEWER}
        width="660px"
        title="Document Viewer"
        versionList={versionList}
        documentObject={selectedItem}
        pdfFile={pdfFile}
        loadDocument={this.loadDocument}
        hideModal={this.handleHideModal}
        handleShowModal={this.handleShowModal}
        page={page}
        totalPageCount={Math.ceil(pageCount / pageSize)}
        pageSize={pageSize}
        documentId={this.props.documentId}
      />
    );
  }

  onVersionReorder(data) {
    const { documentId, reloadDocumentContent } = this.props;

    updateDocumentVersionOrder(data, documentId)
      .then(() => {
        this.handleShowToast(
          SUCCESS,
          this.props.intl.formatMessage({
            id: 'DocumentVersions.reorderSuccess',
            defaultMessage: 'The document version has been reordered.',
            description: 'Success message when document version is reordered',
          }),
        );
        this.loadDocumentVersions();
        reloadDocumentContent();
      })
      .catch((error) => {
        const message =
          error.response?.data?.Error ??
          this.props.intl.formatMessage({
            id: 'DocumentVersions.reorderError',
            defaultMessage:
              'An error occurred while reordering the document version.',
            description: 'Error message when reordering document version fails',
          });
        this.handleShowToast(ERROR, message);
        this.loadDocumentVersions();
      });
  }

  onVersionEdit(file_name, description) {
    const { reloadDocumentContent, documentId } = this.props;
    const { id } = this.state.selectedItem;

    editDocumentVersion(id, file_name, description, documentId)
      .then(() => {
        this.handleShowToast(
          SUCCESS,
          this.props.intl.formatMessage({
            id: 'DocumentVersions.editSuccess',
            defaultMessage: 'Document version has been updated.',
            description: 'Success message when document version is updated',
          }),
        );
        this.loadDocumentVersions();
        reloadDocumentContent();
      })
      .catch(() =>
        this.handleShowToast(
          ERROR,
          this.props.intl.formatMessage({
            id: 'DocumentVersions.editError',
            defaultMessage: 'An error occurred while editing document version.',
            description: 'Error message when editing document version fails',
          }),
        ),
      );
  }

  handleFileAttachment() {
    this.setState({ fileAttached: this.refs.fileContainer.files[0] }, () => {
      const blob = new Blob([this.state.fileAttached], {
        type: 'application/json',
      });
      const fileData = new FormData();
      fileData.append('file', new File([blob], this.state.fileAttached.name));
      this.onVersionReplace(this.refs.fileContainer.files[0]);
    });
  }

  onVersionReplace(item, file) {
    const { documentId } = this.props;
    const blob = new Blob([file], {
      type: 'application/json',
    });
    const fileData = new FormData();
    fileData.append('file', new File([blob], file.name));

    replaceDocumentVersion(item.id, fileData, documentId)
      .then(() => {
        this.handleShowToast(
          SUCCESS,
          this.props.intl.formatMessage({
            id: 'DocumentVersions.replaceSuccess',
            defaultMessage: 'Document version has been replaced.',
            description: 'Success message when document version is replaced',
          }),
        );
        this.closeWarningModal();
        this.loadDocumentVersions();
        this.reloadDocumentContent();
      })
      .catch(() =>
        this.handleShowToast(
          ERROR,
          this.props.intl.formatMessage({
            id: 'DocumentVersions.replaceError',
            defaultMessage:
              'An error occurred while replacing the document version.',
            description: 'Error message when replacing document version fails',
          }),
        ),
      );
  }

  onVersionReplaceClick = async (item, file) => {
    const highlights = await getHighlights({ version_id: item.id });
    if (highlights?.length) {
      this.setState({
        ...this.state,
        warningModal: { isVisible: true, item, file },
      });
    } else {
      this.onVersionReplace(item, file);
    }
  };

  closeWarningModal = () => {
    this.setState({
      ...this.state,
      warningModal: { isVisible: false, item: null, file: null },
    });
  };

  onVersionRestore(item) {
    const { reloadDocumentContent, documentId } = this.props;

    restoreDocumentVersion(item.id, documentId)
      .then(() => {
        this.handleShowToast(
          SUCCESS,
          this.props.intl.formatMessage({
            id: 'DocumentVersions.restoreSuccess',
            defaultMessage: 'Document version has been restored.',
            description: 'Success message when document version is restored',
          }),
        );
        this.loadDocumentVersions();
        reloadDocumentContent();
      })
      .catch(() =>
        this.handleShowToast(
          ERROR,
          this.props.intl.formatMessage({
            id: 'DocumentVersions.restoreError',
            defaultMessage:
              'An error occurred while restoring the document version.',
            description: 'Error message when restoring document version fails',
          }),
        ),
      );
  }

  onVersionDelete() {
    const { documentId, reloadDocumentContent } = this.props;
    const { id } = this.state.selectedItem;

    deleteDocumentVersion(id, documentId)
      .then(() => {
        this.handleShowToast(
          SUCCESS,
          this.props.intl.formatMessage({
            id: 'DocumentVersions.deleteSuccess',
            defaultMessage: 'Document version has been deleted.',
            description: 'Success message when document version is deleted',
          }),
        );
        this.loadDocumentVersions();
        reloadDocumentContent();
      })
      .catch(() =>
        this.handleShowToast(
          ERROR,
          this.props.intl.formatMessage(
            {
              id: 'DocumentVersions.deleteError',
              defaultMessage:
                'An error occurred while deleting document version #{id}.',
              description:
                'Error message when deleting document version fails. Example: id = "123"',
            },
            { id },
          ),
        ),
      );
  }

  renderEditVersionModal() {
    const { selectedItem } = this.state;
    const title = this.props.intl.formatMessage(
      {
        id: 'DocumentVersions.editTitle',
        defaultMessage: 'Edit {versionTag}',
        description:
          'Title for edit version modal. Example: versionTag = "v1.2"',
      },
      { versionTag: selectedItem.version_tag },
    );

    return (
      <EcModal
        modalType={MODAL_NAME}
        width="560px"
        title={title}
        labelText={this.props.intl.formatMessage({
          id: 'DocumentVersions.fileNameLabel',
          defaultMessage: 'FILE NAME',
          description: 'Label for file name field',
        })}
        includeTextArea={true}
        labelTextArea={this.props.intl.formatMessage({
          id: 'DocumentVersions.descriptionLabel',
          defaultMessage: 'DESCRIPTION',
          description: 'Label for description field',
        })}
        hideModal={this.handleHideModal}
        handleShowModal={this.handleShowModal}
        confirmButtonText={this.props.intl.formatMessage({
          id: 'DocumentVersions.saveButton',
          defaultMessage: 'Save',
          description: 'Save button text',
        })}
        itemsCurrentName={selectedItem ? selectedItem.file_name : ''}
        itemsCurrentDescription={selectedItem ? selectedItem.description : ''}
        handleNameChange={this.onVersionEdit}
        nameMaxLength={DOCUMENT_NAME_MAX_LENGTH}
      />
    );
  }

  renderDeleteVersionModalText() {
    const { selectedItem } = this.state;

    return (
      <div>
        {this.props.intl.formatMessage(
          {
            id: 'DocumentVersions.deleteConfirmation',
            defaultMessage:
              "Are you sure you want to delete version {version} of the document? You won't be able to undo this action.",
            description:
              'Confirmation message for deleting document version. Example: version = "v1.2"',
          },
          { version: selectedItem.version },
        )}
      </div>
    );
  }

  renderDeleteVersionModal() {
    return (
      <Modal
        isVisible={true}
        title={this.props.intl.formatMessage({
          id: 'DocumentVersions.deleteTitle',
          defaultMessage: 'Delete Version?',
          description: 'Title for delete version modal',
        })}
        children={<Text>{this.renderDeleteVersionModalText()}</Text>}
        primaryAction={{
          text: this.props.intl.formatMessage({
            id: 'DocumentVersions.deleteButton',
            defaultMessage: 'Delete',
            description: 'Delete button text',
          }),
          variant: 'danger',
          onClick: () => {
            this.onVersionDelete();
            this.handleHideModal();
          },
        }}
        onHide={this.handleHideModal}
        onCancel={this.handleHideModal}
      />
    );
  }

  renderSearchBox() {
    const { classes, intl } = this.props;
    const { filterValue, versionsData } = this.state;

    return (
      <div className={classes.searchboxContainer}>
        <SearchInput
          aria-describedby="doc-versions-found-status"
          name="doc-versions-search-input"
          debounceTimeout={300}
          id="search-version-input"
          value={filterValue}
          onChange={(filterValue) => this.handleVersionSearch(filterValue)}
          placeholder={intl.formatMessage({
            id: 'DocumentViewerPageVersionsTab.searchVersionsPlaceholder',
            defaultMessage: 'Search versions…',
            description: 'Placeholder text for document versions search input',
          })}
        />
        <div
          aria-live="assertive"
          className="screenReaderText"
          id="doc-versions-found-status"
          role="status"
        >
          {!filterValue.length
            ? null
            : versionsData.length
              ? `${versionsData.length} item(s) found`
              : 'No items found'}
        </div>
      </div>
    );
  }

  renderVersionTable() {
    const { classes, userVisibilityLevel } = this.props;
    const { versionsData, loading, errorLoading, filterValue } = this.state;

    if (loading && !errorLoading) {
      return (
        <div className={classes.loadingContainer}>
          <LoadingSpinner size="medium" />
        </div>
      );
    } else if (errorLoading && !loading) {
      return (
        <div>
          {this.props.intl.formatMessage({
            id: 'DocumentVersions.loadVersionError',
            defaultMessage: 'An error occurred while loading the version data.',
            description: 'Error message when loading version data fails',
          })}
        </div>
      );
    }
    return (
      <EcCard>
        {this.renderSearchBox()}
        <EcVersionsTable
          versionsData={versionsData}
          userVisibilityLevel={userVisibilityLevel}
          filterValue={filterValue}
          handleShowModal={this.handleShowModal}
          handleVersionReorder={this.onVersionReorder}
          handleVersionReplace={this.onVersionReplaceClick}
          handleVersionRestore={this.onVersionRestore}
        />
      </EcCard>
    );
  }

  uploadNewVersion = async () => {
    const { warningModal } = this.state;
    const { uploadDocumentsThunk, documentId } = this.props;
    const { file } = warningModal;

    const fileToUpload = new File([file], file.name, { type: file.type });
    fileToUpload.upload = { uuid: uuidV4() };

    await uploadDocumentsThunk({
      files: [fileToUpload],
      apiConfig: {
        service: 'pilot',
        method: 'post',
        url: `/version/document/${documentId}/`,
      },
    }).unwrap();
    this.closeWarningModal();
    this.loadDocumentVersions();
    this.reloadDocumentContent();
  };

  reloadDocumentContent = () => {
    const { reloadDocumentContent } = this.props;
    setTimeout(() => {
      reloadDocumentContent();
    }, 5000);
  };

  renderPagination() {
    const { page, pageCount, pageSize } = this.state;

    if (pageCount <= pageSize) return null;

    return (
      <EcPaginate
        onPageChange={this.handlePageClick}
        pageCount={Math.ceil(pageCount / pageSize)}
        forcePage={page - 1}
      />
    );
  }

  render() {
    const { classes } = this.props;
    const { modal } = this.state;

    return (
      <Fragment>
        {this.renderVersionTable()}
        <span className={classes.horizontalSeparator} />
        {this.renderPagination()}
        {modal === MODAL_NAME ? this.renderEditVersionModal() : null}
        {modal === MODAL_DELETE ? this.renderDeleteVersionModal() : null}
        {modal === MODAL_VERSION_DOCUMENT_VIEWER
          ? this.renderDocumentViewerModal()
          : null}
        <Modal
          title={this.props.intl.formatMessage({
            id: 'DocumentVersions.warningTitle',
            defaultMessage: 'Warning',
            description: 'Warning modal title',
          })}
          onHide={this.closeWarningModal}
          isVisible={this.state.warningModal.isVisible}
          primaryAction={{
            onClick: () =>
              this.onVersionReplace(
                this.state.warningModal.item,
                this.state.warningModal.file,
              ),
            text: this.props.intl.formatMessage({
              id: 'DocumentVersions.replaceButton',
              defaultMessage: 'Replace',
              description: 'Replace button text',
            }),
            variant: 'danger',
          }}
          secondaryActions={[
            {
              text: this.props.intl.formatMessage({
                id: 'DocumentVersions.uploadNewVersionButton',
                defaultMessage: 'Upload new version',
                description: 'Upload new version button text',
              }),
              onClick: this.uploadNewVersion,
            },
          ]}
          onCancel={this.closeWarningModal}
        >
          <Text>
            {this.props.intl.formatMessage({
              id: 'DocumentVersions.replaceWarningMessage1',
              defaultMessage:
                'Replacing this document version will overwrite comments in the current version. If you need to retain comments in the current versions, upload a new version instead.',
              description:
                'First warning message about replacing document version',
            })}
          </Text>
          <Text>
            {this.props.intl.formatMessage({
              id: 'DocumentVersions.replaceWarningMessage2',
              defaultMessage: 'Do you want to continue ?',
              description: 'Second warning message asking for confirmation',
            })}
          </Text>
        </Modal>
      </Fragment>
    );
  }
}

DocumentVersions.propTypes = {
  classes: PropTypes.object.isRequired,
};

DocumentVersions = connect(null, { uploadDocumentsThunk })(DocumentVersions);

export default injectSheet(styles)(withFlags(injectIntl(DocumentVersions)));
